/**
 * Matches a filter value with a given value. Supports inclusion/exclusion
 * and special matching modes such as language codes.
 *
 * @example
 * matchesFilter('en,nl', 'nl') // true
 * matchesFilter('!en', 'en') // false
 * matchesFilter('!en', 'en-US', 'language') // false
 * matchesFilter('en', 'en-US', 'language') // true
 */
export const matchesFilter = (filterValue: string | undefined, value: string, matchFallback = true, type?: 'language'): boolean => {
  if (!filterValue) return matchFallback;

  const filters = filterValue
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);

  const includes = filters.filter((f) => !f.startsWith('!'));
  const excludes = filters.filter((f) => f.startsWith('!')).map((f) => f.slice(1));

  const matchesSingle = (filter: string, val: string): boolean => {
    if (type === 'language') {
      if (filter === val) return true;

      const filterParts = filter.split('-');
      const valParts = val.split('-');

      const filterBase = filterParts[0];
      const valBase = valParts[0];

      const filterHasRegion = filterParts.length > 1;
      const valHasRegion = valParts.length > 1;

      // Base filter matches variant
      if (!filterHasRegion && valBase === filterBase) return true;

      // Region filter exact match
      // filterValue: 'nl-NL', value: 'nl-NL' -> true
      if (filterHasRegion && val === filter) return true;
      // filterValue: 'nl-NL,nl-BE', value:'nl' -> true
      // filterValue: 'nl-NL,!nl-BE', value:'nl' -> false
      if (filterHasRegion && !valHasRegion && valBase === filterBase) return true;

      return false;
    }

    return filter === val;
  };

  // Excludes have priority
  if (excludes.some((current) => matchesSingle(current, value))) {
    return false;
  }

  if (includes.length > 0) {
    return includes.some((current) => matchesSingle(current, value));
  }

  return matchFallback;
};
